<template>
  <v-dialog v-model="dialog" max-width="1000">
    <template #activator="{ on, attrs }">
      <v-list-item v-bind="attrs" v-on="on">
        <v-list-item-icon>
          <v-icon>mdi-plus</v-icon>
        </v-list-item-icon>
        <v-list-item-title>
          {{ $t("add-management-balance") }}
        </v-list-item-title>
      </v-list-item>
    </template>
    <v-card>
      <v-stepper v-model="step">
        <!-- Header dello stepper -->
        <v-stepper-header>
          <v-stepper-step :complete="step > 1" step="1">
            {{ $t("management-balance-declaration") }}
          </v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step :complete="step > 2" step="2">
            {{ $t("management-balance-splits-declaration") }}
          </v-stepper-step>
        </v-stepper-header>

        <!-- Contenuti degli step -->
        <v-stepper-items>
          <!-- Primo step -->
          <v-stepper-content step="1">
            <v-card-title class="techBlue white--text">
              {{ $t("management-balance-declaration") }}
            </v-card-title>
            <v-card-text class="pt-5">
              <v-row no-gutters>
                <v-col cols="8">
                  <div class="text-center">
                    {{ $t("name") }}
                  </div>
                  <div class="d-flex justify-space-between align-center mt-3">
                    <v-text-field
                      v-model="newManagementBalance.name"
                      outlined
                      dense
                      hide-details
                      :rules="[rules.required]"
                      color="techBlue"
                      background-color="white"
                    />
                  </div>
                </v-col>
                <v-col cols="4">
                  <IncrDecrInput
                    v-model="splitDimensions"
                    :label="$t('split-dimensions-number')"
                    :min="1"
                    :max="1"
                  />
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions>
              <v-btn plain @click="onClose">{{ $t("close") }}</v-btn>
              <v-spacer></v-spacer>
              <v-btn
                :disabled="!validateStep1()"
                color="primary"
                @click="step = 2"
              >
                {{ $t("continue") }}
              </v-btn>
            </v-card-actions>
          </v-stepper-content>

          <!-- Secondo step -->
          <v-stepper-content step="2">
            <v-card-title class="techBlue white--text">
              {{ $t("management-balance-splits-declaration") }}
            </v-card-title>
            <v-card-text class="pt-5">
              <v-row
                v-for="(split, index) in newManagementBalance.splits"
                :key="`split-${index}`"
                class="d-flex justify-center align-center"
              >
                <v-col cols="11">
                  <v-text-field
                    v-model="newManagementBalance.splits[index]"
                    :label="$t('split')"
                    outlined
                    dense
                    hide-details
                    color="techBlue"
                    background-color="white"
                    :rules="[rules.required]"
                    class="flex-grow-1"
                  ></v-text-field>
                </v-col>
                <!-- Il bottone di rimozione compare solo per i campi aggiunti dinamicamente (non per il primo) -->
                <v-col cols="1" class="d-flex align-center justify-center">
                  <v-btn v-if="index !== 0" icon @click="removeSplit(index)">
                    <v-icon color="red">mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
              <!-- Bottone per aggiungere nuovi campi -->
              <div class="d-flex justify-center align-center mt-3">
                <v-btn icon @click="addSplit">
                  <v-icon color="green">mdi-plus</v-icon>
                </v-btn>
              </div>
            </v-card-text>
            <v-card-actions>
              <v-btn plain @click="onClose">{{ $t("close") }}</v-btn>
              <v-spacer></v-spacer>
              <v-btn plain @click="step -= 1">
                {{ $t("back") }}
              </v-btn>
              <v-btn
                :disabled="!validateStep2()"
                color="primary"
                @click="createManagementBalance"
              >
                {{ $t("create") }}
              </v-btn>
            </v-card-actions>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>
    </v-card>
  </v-dialog>
</template>

<script>
import { ManagementBalanceService } from "@/services";
import IncrDecrInput from "@/components/fields/IncrDecrInput.vue";
import { Validators } from "@/utilities/validators.js";
import Obj from "@/utilities/obj.js";

export default {
  name: "ManagementBalanceCreationDialog",
  components: { IncrDecrInput },
  props: {
    businessPlanId: {
      type: Number,
      required: true,
    },
  },
  emits: ["input", "save"],
  data() {
    return {
      dialog: false,
      step: 1,

      splitDimensions: 1,

      newManagementBalance: {
        id: 0,
        businessPlanId: null,
        name: "",
        splits: [""],
        data: {},
        version: 1,
        createdAt: null,
        deletedAt: null,
      },

      rules: { ...Validators.rules },
    };
  },
  methods: {
    validateStep1() {
      return this.newManagementBalance.name !== "";
    },
    validateStep2() {
      if (Array.isArray(this.newManagementBalance.splits)) {
        return this.newManagementBalance.splits.every((split) => split !== "");
      } else {
        return true;
      }
    },
    onClose() {
      this.dialog = false;
    },
    addSplit() {
      this.newManagementBalance.splits.push("");
    },
    removeSplit(index) {
      // Rimuoviamo il campo se non è il primo (index 0)
      if (index !== 0 && index) {
        this.newManagementBalance.splits.splice(index, 1);
      }
    },
    async createManagementBalance() {
      if (this.businessPlanId) {
        let managementBalance = Obj.complexObjClone(this.newManagementBalance);
        /* Set businessPlanId */
        managementBalance.businessPlanId = this.businessPlanId;
        /* Set createdAt */
        const currentDate = Date.now();
        const today = new Date(currentDate);
        managementBalance.createdAt = today;
        /* Stringify splits */
        managementBalance.splits = JSON.stringify(managementBalance.splits);
        /* Stringify data */
        managementBalance.data = JSON.stringify(managementBalance.data);

        const managementBalanceId = await ManagementBalanceService.save(
          managementBalance
        );

        this.$router.push({
          name: "balances.management.edit",
          params: { managementBalanceId: managementBalanceId },
        });
      }
    },
  },
};
</script>
